import React, { useEffect, useState } from "react";
import { displayName } from "@/lib/names";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FilePlus2, Loader2 } from "lucide-react";
import { PersonPicker } from "@/components/PersonPicker";

const TEXT_FIELDS = [
  ["first_name", "Prénom"], ["last_name", "Nom de famille"], ["nickname", "Surnom"], ["birth_place", "Lieu de naissance"],
];

export const ContributionDialog = ({ person, allPersons = [], clans = [], existing, trigger, onDone }) => {
  const { API } = useAuth();
  const [open, setOpen] = useState(false);
  const [changes, setChanges] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [extra, setExtra] = useState([]);

  useEffect(() => {
    if (open) { setChanges(existing?.changes || {}); setNote(existing?.note || ""); }
  }, [open, existing]);

  const set = (k, v) => setChanges(prev => ({ ...prev, [k]: v }));
  const options = [...allPersons, ...extra].filter(p => p.id !== person.id);

  const submit = async () => {
    setBusy(true);
    try {
      if (existing) await axios.put(`${API}/contributions/${existing.id}`, { changes, note });
      else await axios.post(`${API}/persons/${person.id}/contributions`, { changes, note });
      toast.success("Proposition envoyée — elle sera examinée avant intégration");
      setOpen(false);
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  };

  return (
    <>
      {trigger ? trigger(() => setOpen(true)) : (
        <Button size="sm" variant="outline" className="border-[#993D22] text-[#993D22]" onClick={() => setOpen(true)} data-testid={`propose-btn-${person.id}`}>
          <FilePlus2 className="w-4 h-4 mr-1" /> Proposer un complément
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FAF7F2] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="contribution-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif-heritage text-2xl text-[#261911]">Compléter « {displayName(person)} »</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6E5B4F]">Ne remplissez que ce que vous savez. Votre proposition sera examinée par un ancien ou un administrateur avant d'être intégrée à la fiche.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {TEXT_FIELDS.map(([k, label]) => (
              <div key={k}>
                <Label>{label}</Label>
                <Input value={changes[k] || ""} placeholder={person[k] || ""} onChange={e => set(k, e.target.value)} data-testid={`contrib-${k}`} />
              </div>
            ))}
            <div>
              <Label>Année de naissance</Label>
              <Input type="number" value={changes.birth_year || ""} placeholder={person.birth_year || ""} onChange={e => set("birth_year", e.target.value)} data-testid="contrib-birth_year" />
              <label className="flex items-center gap-2 text-xs text-[#6E5B4F] mt-1">
                <input type="checkbox" checked={!!changes.birth_year_approx} onChange={e => set("birth_year_approx", e.target.checked)} data-testid="contrib-birth_year_approx" /> Date approximative
              </label>
            </div>
            <div>
              <Label>Année de décès</Label>
              <Input type="number" value={changes.death_year || ""} placeholder={person.death_year || ""} onChange={e => set("death_year", e.target.value)} data-testid="contrib-death_year" />
            </div>
            <div>
              <Label>Famille (Mvog)</Label>
              <select className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm" value={changes.clan_key || ""} onChange={e => set("clan_key", e.target.value)} data-testid="contrib-clan_key">
                <option value="">—</option>
                {clans.map(c => <option key={c.key} value={c.key}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Genre</Label>
              <select className="w-full h-9 rounded-md border border-input bg-transparent px-3 text-sm" value={changes.gender || ""} onChange={e => set("gender", e.target.value)} data-testid="contrib-gender">
                <option value="">—</option><option value="male">Homme</option><option value="female">Femme</option>
              </select>
            </div>
            <div>
              <Label>Père</Label>
              <PersonPicker value={changes.father_id || ""} onChange={v => set("father_id", v)} options={options.filter(p => p.gender !== "female")} gender="male" onCreated={p => setExtra(x => [...x, p])} testid="contrib-father" />
            </div>
            <div>
              <Label>Mère</Label>
              <PersonPicker value={changes.mother_id || ""} onChange={v => set("mother_id", v)} options={options.filter(p => p.gender !== "male")} gender="female" onCreated={p => setExtra(x => [...x, p])} testid="contrib-mother" />
            </div>
          </div>
          <div>
            <Label>Biographie / récit à ajouter</Label>
            <Textarea rows={3} value={changes.biography || ""} onChange={e => set("biography", e.target.value)} data-testid="contrib-biography" />
          </div>
          <div>
            <Label>Source ou commentaire (qui vous l'a dit, document…)</Label>
            <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} data-testid="contrib-note" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
            <Button className="btn-terra" onClick={submit} disabled={busy} data-testid="submit-contribution-btn">
              {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Envoyer la proposition
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ContributionDialog;
